export type Service = {
  slug: string;
  path: string;
  name: string;
  navName: string;
  homeImage: string;
  summary: string;
  homeBlurb: string;
  details: string[];
  metaTitle: string;
  metaDescription: string;
};

export const services: Service[] = [
  {
    slug: 'acupuncture',
    path: '/acupuncture-bradford',
    name: 'Acupuncture in Bradford',
    navName: 'Acupuncture',
    homeImage: '/accupuncture.png',
    summary:
      'Physiotherapy-led acupuncture in Bradford to help manage pain, improve mobility, and support recovery.',
    homeBlurb:
      'We offer acupuncture in Bradford as part of physiotherapy treatment to help reduce pain, improve movement, and support recovery from musculoskeletal conditions.',
    metaTitle: 'Acupuncture Bradford | YH Physio & Performance',
    metaDescription:
      'Physiotherapy-led acupuncture in Bradford to help manage pain, improve mobility, and support recovery from musculoskeletal conditions and sports injuries.',
    details: [
      'Acupuncture is a treatment used at YH Physio & Performance in Bradford to help reduce pain, relieve muscle tension, and support recovery from musculoskeletal and sports injuries.',
      'It is often used alongside physiotherapy to help treat conditions such as back pain, neck pain, shoulder pain, joint pain, and sports injuries.',
      'Each treatment is based on a full assessment to ensure it is appropriate for your condition and is tailored to your individual needs.',
      'Acupuncture is commonly combined with exercise rehabilitation and manual therapy to help improve movement, reduce symptoms, and prevent the problem from reoccurring.',
      'If you are looking for acupuncture in Bradford, YH Physio & Performance can help as part of a personalised treatment plan.',
      'Book acupuncture in Bradford today to support your recovery and reduce pain.'
    ]
  },
  {
    slug: 'sports-massage',
    path: '/sports-massage-bradford',
    name: 'Sports Massage in Bradford',
    navName: 'Sports Massage',
    homeImage: '/massage.png',
    summary:
      'Professional sports massage in Bradford to help reduce muscle tension, improve recovery, and support performance.',
    homeBlurb:
      'We provide sports massage in Bradford to help reduce muscle tension, improve recovery, and support movement and performance. Sports massage may help with muscular aches, stiffness, sports injuries, and post-exercise recovery.',
    metaTitle: 'Sports Massage Bradford | YH Physio & Performance',
    metaDescription:
      'Professional sports massage in Bradford to help reduce muscle tension, improve recovery, and support movement, rehabilitation, and performance.',
    details: [
      'Sports massage at YH Physio & Performance in Bradford is designed to help reduce muscle tension, improve recovery, and support performance.',
      'Treatment focuses on areas of tightness or overuse and can help with muscle soreness, stiffness, and sports-related injuries. It is often used alongside physiotherapy and rehabilitation exercises for better long-term results.',
      'Sports massage can be beneficial for people with regular training loads, gym-related muscle tightness, or those recovering from sports injuries or physical strain.',
      'Each session is tailored to your needs following a brief assessment to ensure the treatment is appropriate and effective for your condition.'
    ]
  },
  {
    slug: 'injection-therapy',
    path: '/injection-therapy-bradford',
    name: 'Injection Therapy in Bradford',
    navName: 'Injection Therapy',
    homeImage: '/injection.png',
    summary:
      'Injection therapy in Bradford provided alongside physiotherapy to help reduce pain, inflammation, and support rehabilitation.',
    homeBlurb:
      'We provide injection therapy in Bradford as an adjunct to physiotherapy treatment to help manage pain, reduce inflammation, and support rehabilitation for musculoskeletal conditions. Injection therapy may be used alongside physiotherapy for conditions including osteoarthritis, bursitis, tendon-related pain, and joint inflammation.',
    metaTitle: 'Injection Therapy Bradford | YH Physio & Performance',
    metaDescription:
      'Injection therapy in Bradford provided alongside physiotherapy treatment to help reduce pain, inflammation, and support musculoskeletal rehabilitation.',
    details: [
      'Injection therapy at YH Physio & Performance in Bradford is offered as an adjunct to physiotherapy treatment to help reduce pain and support recovery in selected musculoskeletal conditions.',
      'It may be used where pain or inflammation is limiting progress with rehabilitation and is always guided by a full physiotherapy assessment.',
      'Injection therapy can be considered for conditions such as osteoarthritis, shoulder bursitis and tendinopathies (including rotator cuff-related pain), carpal tunnel syndrome, De Quervain’s tenosynovitis, bursitis, and trigger finger.',
      'At YH Physio & Performance, injection therapy is never used in isolation. It is combined with exercise-based rehabilitation, manual therapy, and movement retraining to address the underlying cause of symptoms and improve long-term outcomes.',
      'All treatment decisions are based on clinical assessment to ensure it is appropriate, safe, and tailored to the individual.'
    ]
  },
  {
    slug: 'desk-assessment',
    path: '/desk-assessment-bradford',
    name: 'Desk Assessment – Workplace Ergonomic Assessment in Bradford',
    navName: 'Desk Assessment',
    homeImage: '/desk.png',
    summary:
      'Desk and workstation assessments in Bradford to help improve posture, reduce pain, and optimise your working environment.',
    homeBlurb:
      'We provide desk and workstation assessments in Bradford to help reduce pain, improve posture, and optimise your working environment. This service may help with neck pain, back pain, shoulder tension, and posture-related discomfort linked to prolonged desk work.',
    metaTitle: 'Desk Assessment Bradford | Workstation Assessment Bradford',
    metaDescription:
      'Desk and workstation assessments in Bradford to help improve posture, reduce pain, and optimise your working environment and ergonomics.',
    details: [
      'At YH Physio & Performance, we provide professional desk assessments designed to reduce pain, improve posture, and help prevent workplace-related injuries. Whether you work from home or in an office environment, prolonged sitting and poor workstation setup can contribute to neck pain, back pain, shoulder tension, headaches, and repetitive strain injuries.',
      'Our desk assessment service in Bradford evaluates your workstation setup, posture, movement habits, and daily working patterns to identify factors contributing to discomfort or reduced performance.',
      'Book an assessment today to optimise your workstation and support your health at work.'
    ]
  },
  {
    slug: 'walking-aid-assessment',
    path: '/walking-aid-assessment-bradford',
    name: 'Walking Aid Assessment Bradford',
    navName: 'Walking Aid Assessment',
    homeImage: '/cane.png',
    summary:
      'Physiotherapy-led walking aid assessments in Bradford to improve mobility, balance, confidence, and safe use of walking aids.',
    homeBlurb:
      'We provide walking aid assessments in Bradford to help improve mobility, balance, and confidence. Our physiotherapy-led assessments include advice on suitable walking aids, correct fitting, and safe use to support independence and reduce falls risk.',
    metaTitle: 'Walking Aid Assessment Bradford | YH Physio & Performance',
    metaDescription:
      'Physiotherapy-led walking aid assessments in Bradford to improve mobility, balance, confidence, and safe use of walking aids.',
    details: [
      'YH Physio & Performance provides professional walking aid assessments in Bradford to help improve mobility, balance, and confidence. We assess your walking pattern, strength, and stability to recommend the most suitable walking aid for your needs.',
      'This may include walking sticks, crutches, or frames, along with advice on safe use and correct fitting.'
    ]
  },
  {
    slug: 'online-physiotherapy',
    path: '/online-physiotherapy-uk',
    name: 'Online Physiotherapy UK – Video & Telephone Consultations',
    navName: 'Online Physiotherapy',
    homeImage: '/mobile.png',
    summary:
      'Online physiotherapy in the UK via video and telephone consultations offering expert assessment, triage, and rehabilitation support worldwide.',
    homeBlurb:
      'We provide online physiotherapy in the UK via video and telephone consultations, offering expert assessment, triage, and rehabilitation support worldwide. This service is ideal for musculoskeletal pain, sports injuries, movement issues, and ongoing rehabilitation from the comfort of your own home.',
    metaTitle: 'Online Physiotherapy UK | Video & Telephone Consultations',
    metaDescription:
      'Online physiotherapy in the UK via video and telephone consultations offering expert assessment, triage, and rehabilitation support worldwide.',
    details: [
      'YH Physio & Performance Bradford provides online physiotherapy in the UK via video and telephone consultations, offering expert assessment, injury triage, and rehabilitation support worldwide.'
    ]
  }
];

export function getServiceByPath(path: string): Service | undefined {
  return services.find((service) => service.path === path);
}

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find((service) => service.slug === slug);
}
